import {
  bffCrmScheduleDrafts,
  BffCrmScheduleDraftsCreateBodyParams,
  bffCrmScheduleDraftsPending,
  BffCrmScheduleDraftsPendingRetrieveResponse,
  bffCrmScheduleDraftsStatus,
  BffCrmScheduleDraftsStatusCreateBodyParams,
  bffCrmScheduleHistoricalShiftExceptions,
  BffCrmScheduleHistoricalShiftExceptionsRetrieveResponse,
  bffCrmScheduleHistoricalShifts,
  BffCrmScheduleHistoricalShiftsRetrieveResponse,
  RequestConfig,
  scheduleAppointmentTypes,
  schedulePhysicianAppointmentTypeMappings,
  scheduleV2Events,
  scheduleVisitMixRatios,
  ScheduleVisitMixRatiosCreateBodyParams,
  ScheduleVisitMixRatiosPartialUpdateBodyParams,
  userProvidersSchedule,
} from '@fireflyhealth/api'
import { useMutation } from '~/shared/hooks/query'
import {
  AppointmentType,
  Schedule,
  ScheduleDraftResponse,
  IngestionImpact,
  Shift,
  ShiftException,
  mapAppointmentTypes,
  mapSchedule,
} from './ProviderShift/utils'

export const getAppointmentTypes = async (): Promise<AppointmentType[]> => {
  const response = await scheduleAppointmentTypes.list()
  return mapAppointmentTypes(response.data)
}

export const getSchedulesForProvider = async (providerId: number): Promise<Schedule[]> => {
  const response = await userProvidersSchedule.list({ provider_id: providerId })
  return response.data.map(mapSchedule)
}

// `getHistoricalShifts` now reads the typed BFF response directly via the
// generated client (S-B1/S-B5), so the prior camelCase adapter is gone. The
// wire range carries only lower/upper, so (like mapEffectivePeriod/mapPeriod)
// the half-open `bounds` the UI date widgets expect is synthesized here. The
// wire `effective_period` is nullable, so coerce a missing range to '' to keep
// the string-typed working model sound.
type HistoricalShiftResponse =
  BffCrmScheduleHistoricalShiftsRetrieveResponse['historical_shifts'][number]

const adaptHistoricalShift = (shift: HistoricalShiftResponse): Shift => ({
  id: shift.id,
  day_of_week: shift.day_of_week,
  start_time: shift.start_time,
  stop_time: shift.stop_time,
  effective_period: {
    lower: shift.effective_period?.lower ?? '',
    upper: shift.effective_period?.upper ?? null,
    bounds: '[)',
  },
  isEdited: false,
})

// `bffCrmScheduleHistoricalShiftExceptions.retrieve` returns snake_case directly.
// `period` is now typed (S-B5) but nullable at the object level for open-ended
// exceptions, so default lower/upper to '' and synthesize the half-open `bounds`
// the UI's date widgets expect.
type HistoricalShiftExceptionResponse =
  BffCrmScheduleHistoricalShiftExceptionsRetrieveResponse['historical_shift_exceptions'][number]

const adaptHistoricalShiftException = (
  exception: HistoricalShiftExceptionResponse,
): ShiftException => ({
  id: exception.id,
  period: {
    lower: exception.period?.lower ?? '',
    upper: exception.period?.upper ?? '',
    bounds: '[)',
  },
  reason: exception.reason,
  isEdited: false,
})

const getHistoricalShifts = async (providerId: number, dayOfWeek: number) => {
  // `provider_id` and `day_of_week` are required query params on this BFF
  // endpoint but are not modeled in openapi.yaml (S-B1/S-B5 typed only the
  // response), so type the config explicitly to pass them through retrieve.
  const config: RequestConfig<{ provider_id: number; day_of_week: number }, undefined, true> = {
    params: { provider_id: providerId, day_of_week: dayOfWeek },
  }
  const response = await bffCrmScheduleHistoricalShifts.retrieve(
    config as Parameters<typeof bffCrmScheduleHistoricalShifts.retrieve>[0],
  )
  return {
    ...response.data,
    historical_shifts: response.data.historical_shifts.map(adaptHistoricalShift),
  }
}

const getHistoricalShiftExceptions = async (providerId: number) => {
  // `provider_id` is a required query param on this BFF endpoint but is not yet
  // modeled in openapi.yaml (S-B3 typed only the response), so type the config
  // explicitly to pass it through the generated retrieve.
  const config: RequestConfig<{ provider_id: number }, undefined, true> = {
    params: { provider_id: providerId },
  }
  const response = await bffCrmScheduleHistoricalShiftExceptions.retrieve(
    config as Parameters<typeof bffCrmScheduleHistoricalShiftExceptions.retrieve>[0],
  )
  return {
    ...response.data,
    historical_shift_exceptions: response.data.historical_shift_exceptions.map(
      adaptHistoricalShiftException,
    ),
  }
}

const createScheduleIngestionJob = async (
  payload: BffCrmScheduleDraftsCreateBodyParams,
): Promise<ScheduleDraftResponse> => {
  const response = await bffCrmScheduleDrafts.create({ data: payload })
  // `id` is optional on the wire (the BFF omits it when `success` is false, e.g.
  // a validation failure), but every caller needs it to track the new draft, so
  // fail loudly rather than coercing an absent id with `!`.
  if (!response.data.success || response.data.id == null) {
    throw new Error(response.data.message || 'Failed to create schedule ingestion job')
  }
  return {
    success: response.data.success,
    message: response.data.message,
    id: response.data.id,
  }
}

const getScheduleIngestionJob = async (ingestionJobId: number): Promise<IngestionImpact> => {
  const response = await bffCrmScheduleDrafts.retrieve({ job_id: ingestionJobId })
  return response.data.ingestion_impact
}

export const useGetScheduleIngestionJob = () => {
  return useMutation(
    getScheduleIngestionJob,
    {},
    { success: 'Updated schedule ingestion job', error: 'Failed to update schedule ingestion job' },
  )
}

export const useGetHistoricalShifts = () => {
  return useMutation(
    ({ providerId, dayOfWeek }: { providerId: number; dayOfWeek: number }) =>
      getHistoricalShifts(providerId, dayOfWeek),
    {},
    { error: 'Failed to load historical shifts' },
  )
}

export const useGetHistoricalShiftExceptions = () => {
  return useMutation(
    ({ providerId }: { providerId: number }) => getHistoricalShiftExceptions(providerId),
    {},
    { error: 'Failed to load historical shift exceptions' },
  )
}

export const useCreateScheduleIngestionJob = () => {
  return useMutation(
    createScheduleIngestionJob,
    {},
    { success: 'Created schedule ingestion job', error: 'Failed to create schedule ingestion job' },
  )
}

const updateIngestionJobStatus = async ({
  jobId,
  ...data
}: { jobId: number } & BffCrmScheduleDraftsStatusCreateBodyParams): Promise<void> => {
  await bffCrmScheduleDraftsStatus.create({ job_id: jobId }, { data })
}

export const useUpdateIngestionJobStatus = () => {
  return useMutation(
    updateIngestionJobStatus,
    {},
    { success: 'Updated ingestion job', error: 'Failed to update ingestion job' },
  )
}

const createOrUpdateVisitMix = async (
  payload: { id: number | null } & ScheduleVisitMixRatiosCreateBodyParams,
): Promise<void> => {
  if (payload.id != null) {
    const data: ScheduleVisitMixRatiosPartialUpdateBodyParams = {
      percentage_of_slots: payload.percentage_of_slots,
    }
    await scheduleVisitMixRatios.partialUpdate({ id: payload.id }, { data })
  } else {
    const { id: _id, ...data } = payload
    await scheduleVisitMixRatios.create({ data })
  }
}

export const useCreateOrUpdateVisitMix = () => {
  return useMutation(createOrUpdateVisitMix, {}, { error: 'Failed to save visit mix ratios' })
}

// Physician–appointment type mappings
type PhysicianAppointmentTypeMappingPayload = {
  physicianId: number
  appointmentTypeId: number
}

const createPhysicianAppointmentTypeMapping = async (
  payload: PhysicianAppointmentTypeMappingPayload,
): Promise<true> => {
  await schedulePhysicianAppointmentTypeMappings.create({
    physician_id: payload.physicianId,
    appointment_type_id: payload.appointmentTypeId,
  })
  return true
}

const deletePhysicianAppointmentTypeMapping = async (
  payload: PhysicianAppointmentTypeMappingPayload,
): Promise<true> => {
  await schedulePhysicianAppointmentTypeMappings.destroy({
    physician_id: payload.physicianId,
    appointment_type_id: payload.appointmentTypeId,
  })
  return true
}

export const useCreateAppointmentTypeMapping = () => {
  return useMutation(
    createPhysicianAppointmentTypeMapping,
    {},
    {
      success: 'Appointment type mapped',
      error: 'Failed to map appointment type',
    },
  )
}

export const useDeleteAppointmentTypeMapping = () => {
  return useMutation(
    deletePhysicianAppointmentTypeMapping,
    {},
    {
      success: 'Appointment type unmapped',
      error: 'Failed to unmap appointment type',
    },
  )
}

const stripMilliseconds = (date: Date) => date.toISOString().replace(/\.\d+/, '')

export const listEventsForProvider = async (providerId: number, start: Date, end: Date) => {
  const { data } = await scheduleV2Events.retrieve(
    { provider_id: providerId },
    { params: { start_date: stripMilliseconds(start), end_date: stripMilliseconds(end) } },
  )

  return data.events
}

const getPendingScheduleDrafts = async (): Promise<BffCrmScheduleDraftsPendingRetrieveResponse> => {
  const { data } = await bffCrmScheduleDraftsPending.retrieve()
  return data
}

export const useGetPendingScheduleDrafts = () => {
  return useMutation(
    getPendingScheduleDrafts,
    {},
    { success: 'Loaded pending schedule drafts', error: 'Failed to load pending schedule drafts' },
  )
}
